import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  DidInventoryItem,
  generateDidRange,
  generateManualConfig,
  seedDidItem
} from "../domain";
import "./styles.css";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
const cognitoClientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
const redirectUri =
  (import.meta.env.VITE_COGNITO_REDIRECT_URI as string | undefined) || window.location.origin;

const previewRows: DidInventoryItem[] = generateDidRange().map((row) => {
  const item = seedDidItem(row.did, "preview");
  if (row.did === "047777600") {
    return {
      ...item,
      status: "assigned",
      assigned_user: {
        name: "Vendor PA Customer",
        email: "customer@example.com",
        notes: "Example customer number for PA dashboard handoff."
      },
      assigned_agent_id: "agent_vendor_customer_pa",
      assistant_profile: {
        type: "vendor_pa",
        display_name: "Customer Personal Assistant",
        owner: "PA Vendor"
      },
      vendor_profile: {
        vendor_name: "PA Vendor",
        contact_email: "vendor@example.com",
        pa_dashboard_customer_id: "customer-123",
        current_provider: "twilio",
        target_provider: "elevenlabs_sip"
      }
    };
  }
  return item;
});

function App() {
  const [query, setQuery] = useState("");
  const [selectedDid, setSelectedDid] = useState("047777666");
  const [remoteRows, setRemoteRows] = useState<DidInventoryItem[] | null>(null);
  const [apiStatus, setApiStatus] = useState<string>("Preview data loaded");
  const idToken = getIdToken();

  React.useEffect(() => {
    if (!apiBaseUrl || !idToken) return;
    fetch(`${apiBaseUrl.replace(/\/$/, "")}/api/dids`, {
      headers: { authorization: `Bearer ${idToken}` }
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`API ${response.status}`);
        return response.json() as Promise<{ items: DidInventoryItem[] }>;
      })
      .then((body) => {
        setRemoteRows(body.items);
        setApiStatus("Connected to AWS DID backend");
      })
      .catch((err) => setApiStatus(`AWS API not connected: ${err instanceof Error ? err.message : err}`));
  }, [idToken]);

  const rows = remoteRows || previewRows;

  const filteredRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      [row.did, row.e164, row.status, row.assigned_user?.email, row.assigned_user?.name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [query, rows]);

  const selected = rows.find((row) => row.did === selectedDid) || rows[0]!;
  const snapshot = generateManualConfig(selected);

  return (
    <main className="shell">
      <header className="hero">
        <div>
          <p className="eyebrow">Meydan Calls Console</p>
          <h1>DID provisioning ledger</h1>
          <p className="heroText">
            Assign `047777600-699`, generate safe PBX and ElevenLabs handoff config,
            and protect the working `047777666` baseline.
          </p>
        </div>
        <div className="baselineCard">
          <span className="badge live">Golden reference</span>
          <strong>047777666</strong>
          <span>Two-way audio verified</span>
          <small>{apiStatus}</small>
          {!idToken && cognitoDomain && cognitoClientId ? (
            <button className="loginButton" onClick={startLogin}>
              Sign in
            </button>
          ) : null}
        </div>
      </header>

      <section className="prereq">
        <div>
          <h2>PBX prerequisite</h2>
          <p>One manual wildcard route covers the full range. Do not change trunk media settings.</p>
        </div>
        <code>To_ElevenLabs · 0477776XX · strip 1 · prepend +971</code>
      </section>

      <section className="workspace">
        <div className="panel tablePanel">
          <div className="panelHeader">
            <h2>DID inventory</h2>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search DID, assignee, status..."
            />
          </div>
          <table>
            <thead>
              <tr>
                <th>DID</th>
                <th>E.164</th>
                <th>Status</th>
                <th>Assignee</th>
                <th>Agent</th>
                <th>Test</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.did}
                  className={row.did === selected.did ? "selected" : ""}
                  onClick={() => setSelectedDid(row.did)}
                >
                  <td>
                    <strong>{row.did}</strong>
                    {row.protection === "golden_reference" ? <span className="mini">locked</span> : null}
                  </td>
                  <td>{row.e164}</td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                  <td>{row.assigned_user?.name || "Unassigned"}</td>
                  <td>{row.assistant_profile?.type || "meydan_operator"}</td>
                  <td>{row.last_test_result.replaceAll("_", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="panel detailPanel">
          <div className="panelHeader">
            <div>
              <p className="eyebrow">Selected DID</p>
              <h2>{selected.e164}</h2>
            </div>
            <StatusBadge status={selected.status} />
          </div>

          {selected.protection === "golden_reference" ? (
            <div className="notice">
              This is the working voice path. The MVP console must not mutate this DID.
            </div>
          ) : null}

          <section className="detailSection">
            <h3>Assignment</h3>
            <dl>
              <div>
                <dt>User</dt>
                <dd>{selected.assigned_user?.name || "Not assigned"}</dd>
              </div>
              <div>
                <dt>Agent ID</dt>
                <dd>{selected.assigned_agent_id}</dd>
              </div>
              <div>
                <dt>Assistant</dt>
                <dd>{selected.assistant_profile?.type || "meydan_operator"}</dd>
              </div>
              <div>
                <dt>Vendor</dt>
                <dd>{selected.vendor_profile?.vendor_name || "N/A"}</dd>
              </div>
            </dl>
          </section>

          <section className="detailSection">
            <h3>Verification gate</h3>
            <ul className="checklist">
              <li>PBX dial pattern: {selected.pbx_dial_pattern_status}</li>
              <li>PBX route: {selected.pbx_route_status}</li>
              <li>ElevenLabs: {selected.elevenlabs_status}</li>
              <li>Call test: {selected.last_test_result}</li>
            </ul>
          </section>

          <section className="detailSection">
            <h3>ElevenLabs handoff</h3>
            <pre>{JSON.stringify(snapshot.elevenlabs, null, 2)}</pre>
          </section>
        </aside>
      </section>
    </main>
  );
}

function startLogin() {
  if (!cognitoDomain || !cognitoClientId) return;
  const params = new URLSearchParams({
    client_id: cognitoClientId,
    response_type: "token",
    scope: "openid email profile",
    redirect_uri: redirectUri
  });
  window.location.href = `https://${cognitoDomain}/oauth2/authorize?${params.toString()}`;
}

function getIdToken(): string | null {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token = hash.get("id_token");
  if (token) {
    window.localStorage.setItem("calls-console-id-token", token);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    return token;
  }
  return window.localStorage.getItem("calls-console-id-token");
}

function StatusBadge({ status }: { status: DidInventoryItem["status"] }) {
  return <span className={`badge ${status}`}>{status.replaceAll("_", " ")}</span>;
}

createRoot(document.getElementById("root")!).render(<App />);
